import Layout from 'components/layout';

import React from 'react';

import { css } from '@emotion/react';
import { i18nFunct, Link, getProcessedValue, linkParser } from '@lucid/gatsby-theme-lucid';
import { graphql } from 'gatsby';
import { DomElement } from 'htmlparser2';
import { Box, Breadcrumb, Heading, Paper } from 'lucid-components-react';
import ReactHtmlParser from 'react-html-parser';
import {
  Drupal_FieldNodeCaseStudyFieldAdditionalResources,
  Drupal_FieldNodeCaseStudyFieldAdditionalResourcesCtas,
  Drupal_FieldNodeCaseStudyFieldHeaderCta,
  Drupal_FieldNodeCaseStudyFieldIndustry,
  Drupal_FieldNodeCaseStudyFieldKeyBenefits,
  Drupal_FieldNodeCaseStudyFieldRole,
  Drupal_FieldNodeCaseStudyFieldSize,
  Drupal_FieldParagraphCtaButtonFieldMarketo,
  Drupal_NodeCaseStudy,
  Drupal_ParagraphAdditionalResources,
  Drupal_ParagraphCtaButton,
  Drupal_ParagraphKeyBenefits,
  Drupal_ParagraphMarketoForm,
  Drupal_TaxonomyTermIndustry,
  Drupal_TaxonomyTermRole
} from 'generated/drupal-graphql';

import AdditionalResources from 'components/additional-resources/additional-resources';
import useMarketoLink from 'hooks/use-marketo-link';
import { useMenuTopNavigation } from 'hooks/use-menu';
import { Text } from 'theme/typography';
import { LucidTheme, TemplateProps } from 'types/types';
import { getEntityFields } from 'utils/getEntityFields';
import { mqMax } from 'theme/base';

const contentsCss = (theme: LucidTheme) => css({
  maxWidth: 440,
  display: 'inline-block',
  textAlign: 'left',
  marginRight: theme.space[10],
  paddingLeft: theme.space[9],
  [mqMax.maxWidth]: {
    marginRight: '20%',
    paddingLeft: theme.space[9]
  },
  [mqMax.computer]: {
    paddingLeft: theme.space[8]
  },
  [mqMax.tabletSmall]: {
    display: 'block',
    maxWidth: '100%',
    paddingLeft: '8.5%',
    paddingRight: '8.5%',
    margin: theme.space[0]
  },
  a: {
    marginTop: theme.space[4],
    marginRight: theme.space[2],
    '&:last-child': {
      marginRight: theme.space[0]
    }
  }
});

const summaryCss = (theme: LucidTheme) => css({
  marginBottom: theme.space[6],
  p: {
    fontSize: theme.fontSizes[3],
    lineHeight: theme.lineHeights.body,
    margin: `${theme.space[1]}px ${theme.space[0]}`
  },
  strong: {
    fontSize: theme.fontSizes[3],
    lineHeight: theme.lineHeights.default,
    fontWeight: theme.fontWeights.bold,
    letterSpacing: '0.05rem'
  }
});

const CaseStudy = ({ data, pageContext, location }: TemplateProps) => {
  const node = data.drupal.nodeById as Drupal_NodeCaseStudy;
  const { i18nData } = data;

  const { i18nKey, hideSsr } = pageContext;

  const i18n = i18nFunct(i18nData, i18nKey);
  let sidebarRef: HTMLElement | null = null;

  const transform = (htmlNode: DomElement) => {
    if (htmlNode.type === 'tag' && htmlNode.name === 'p') {
      if (
        htmlNode.children?.some(
          (child: any) => child.type === 'tag' && child.name === 'img'
        )
      ) {
        htmlNode.name = 'div';
      }
    } else if (
      htmlNode.type === 'tag' &&
      htmlNode.name === 'img' &&
      htmlNode.attribs
    ) {
      const { src, alt } = htmlNode.attribs;

      return (
        <img
          alt={alt}
          loading="lazy"
          onLoad={e => {
              const sidebarBottom =
                sidebarRef!.offsetTop + sidebarRef!.offsetHeight;
              const image = e.target as HTMLImageElement;
              const imgTop = image.offsetTop;
              if (imgTop > sidebarBottom && image.naturalWidth >= 1200) {
                const parent = image.parentNode as HTMLElement;
                parent.classList.add('extend');
              }
            }}
          src={src}
        />
      );
    }
    return linkParser(htmlNode);
  };

  const { langPrefix, pageType, nid } = pageContext;

  const getMarketoLink = useMarketoLink({
    i18nKey,
    langPrefix
  });

  const { entityMetatags, entityTranslations } = getEntityFields(node);

  const backButton = data.drupal.nodeReference;
  const backButtonPath = backButton?.entityUrl
    ? `${backButton.entityUrl.path}${location.search || ''}`
    : null;

  const navbarMenu = useMenuTopNavigation(langPrefix);

  return (
    <Layout
      entityTranslations={entityTranslations}
      hideSsr={hideSsr}
      langPrefix={langPrefix}
      location={location}
      metatags={entityMetatags}
      navbar={{menu: navbarMenu}}
      nid={nid}
      pageType={pageType}
      trackers={{
        personas: node.fieldPersona
      }}
    >
      <Box
        as="main"
        css={(theme: LucidTheme) => ({
          display: 'flex',
          margin: 'auto',
          fontSize: theme.fontSizes[4],
          fontWeight: theme.fontWeights.body,
          lineHeight: theme.lineHeights.body,
          [mqMax.tabletSmall]: {
            flexWrap: 'wrap',
            fontSize: theme.fontSizes[3]
          }
        })}
      >
        <Box
          css={(theme: LucidTheme) => ({
            flex: '1 1 405px',
            textAlign: 'right',
            background: theme.colors.coolGrey[0],
            [mqMax.tabletLandscape]: {
              flex: '1 1 420px'
            },
            [mqMax.tabletSmall]: {
              flex: '0 1 100%',
              maxWidth: '100%'
            }
          })}
        >
          <img
            alt="Banner"
            css={{
              display: 'none',
              [mqMax.tabletSmall]: {
                display: 'block'
              }
            }}
            src={node.fieldHeroImage!}
          />
          <Paper variant="raised">
            <Box
              css={(theme: LucidTheme) => [
                contentsCss(theme),
                {
                  marginRight: theme.space[0],
                  [mqMax.computer]: {
                    paddingLeft: theme.space[4]
                  }
                }
              ]}
            >
              {!node.fieldIsPrivate && backButtonPath && (
                <Box
                  css={(theme: LucidTheme) => ({
                    display: 'block',
                    fontSize: theme.fontSizes[4],
                    marginBottom: theme.space[7],
                    [mqMax.tabletLandscape]: {
                      marginBottom: theme.space[6],
                      fontSize: theme.fontSizes[3]
                    },
                    [mqMax.tabletSmall]: {
                      marginBottom: theme.space[5]
                    }
                  })}
                >
                  <Breadcrumb
                    items={[
                      <Link
                        key={backButtonPath}
                        checkHistory
                        className="hero-card__back"
                        to={backButtonPath}
                      >
                        {i18n('mktg-ct-cs-back-to-hub')}
                      </Link>,
                      node.fieldCompanyName
                    ]}
                  />
                </Box>
              )}
              {node.fieldLogo ? (
                <img
                  alt={node.fieldCompanyName!}
                  css={(theme: LucidTheme) => ({
                    marginBottom: theme.space[3]
                  })}
                  loading="lazy"
                  src={node.fieldLogo}
                />
              ) :
                (
                  <Heading as="h1" variant="headlineXS">
                    {node.fieldTitle}
                  </Heading>
                )}
              <Text textStyles="body">{node.fieldHeroText}</Text>
              {(node.fieldHeaderCta as Drupal_FieldNodeCaseStudyFieldHeaderCta[]).map(
                (cta, i) => {
                  const ctaEntity = cta.entity as Drupal_ParagraphCtaButton;
                  const ctaMarketo = ctaEntity.fieldMarketo as Drupal_FieldParagraphCtaButtonFieldMarketo;
                  const buttonText = ctaEntity.fieldButtonText;
                  const defaultText =
                    i === 0
                      ? i18n('mktg-ct-all-get-a-quote')
                      : i18n('mktg-ct-all-request-a-demo');
                  const mktoId = (ctaMarketo.entity as Drupal_ParagraphMarketoForm)
                    .fieldMarketoId!;
                  if (ctaEntity.fieldButtonLink) {
                    return (
                      <Link
                        key={ctaEntity.fieldButtonLink}
                        displayAsButton
                        primary={!ctaEntity.fieldIsSecondary}
                        secondary={!!ctaEntity.fieldIsSecondary}
                        to={ctaEntity.fieldButtonLink}
                      >
                        {buttonText || defaultText}
                      </Link>
                    );
                  }
                  if (mktoId) {
                    const { url, copy } = getMarketoLink(mktoId);
                    return (
                      <Link
                        key={mktoId}
                        displayAsButton
                        primary={!ctaEntity.fieldIsSecondary}
                        secondary={!!ctaEntity.fieldIsSecondary}
                        to={url}
                      >
                        {buttonText || copy}
                      </Link>
                    );
                  }
                  return;
                }
              )}
            </Box>
          </Paper>
          <Box
            ref={(sidebar: any) => {
              sidebarRef = sidebar;
            }}
            css={contentsCss}
          >
            <Heading
              as="h2"
              css={(theme: LucidTheme) => ({
                marginTop: theme.space[8],
                marginBottom: theme.space[5],
                fontSize: theme.fontSizes[6],
                lineHeight: theme.lineHeights.body,
                fontWeight: theme.fontWeights.subheading,
                flex: '0 1 100%',
                color: theme.colors.coolGrey[100],
                [mqMax.tabletSmall]: {
                  fontSize: theme.fontSizes[5]
                }
              })}
              mb={5}
              mt={8}
              variant="subheadS"
            >
              {node.fieldKeyBenefitsTitle || i18n('mktg-ct-cs-key-benefits')}
            </Heading>
            <dl>
              {(node.fieldKeyBenefits as Drupal_FieldNodeCaseStudyFieldKeyBenefits[]).map(
                benefit => {
                  const benefitEntity = benefit.entity as Drupal_ParagraphKeyBenefits;
                  return (
                    <Box key={benefitEntity.fieldBenefitName}>
                      <dt
                        css={(theme: LucidTheme) => ({
                          fontSize: theme.fontSizes[3],
                          lineHeight: theme.lineHeights.body,
                          fontWeight: theme.fontWeights.bold,
                          letterSpacing: '0.05rem',
                          marginBottom: theme.space[2]
                        })}
                      >
                        {benefitEntity.fieldBenefitName}
                      </dt>
                      <dd
                        css={(theme: LucidTheme) => ({
                          marginLeft: theme.space[0],
                          fontSize: theme.fontSizes[3],
                          lineHeight: theme.lineHeights.body,
                          fontWeight: theme.fontWeights.body,
                          marginBottom: theme.space[6]
                        })}
                      >
                        {benefitEntity.fieldBenefitDescription}
                      </dd>
                    </Box>
                  );
                }
              )}
            </dl>
            <hr
              css={(theme: LucidTheme) => ({
                border: `0.5px solid ${theme.colors.coolGrey[10]}`,
                height: 0,
                margin: `${theme.space[7]}px ${theme.space[0]}`
              })}
            />
            <Box css={summaryCss}>
              {node.fieldIndustry!.length > 0 && (
                <p>
                  <strong>
                    {i18n('mktg-ct-cs-category-1')}
                    :&nbsp;
                  </strong>
                  {(node.fieldIndustry as Drupal_FieldNodeCaseStudyFieldIndustry[]).map(
                    (industry, i) => {
                      const industryEntity = industry.entity as Drupal_TaxonomyTermIndustry;
                      return `${industryEntity.name}${
                        i < node.fieldIndustry!.length - 1 ? ', ' : ''
                        }`;
                    }
                  )}
                </p>
              )}
              {(node.fieldSize as Drupal_FieldNodeCaseStudyFieldSize) && (
                <p>
                  <strong>
                    {i18n('mktg-ct-cs-category-2')}
                    :&nbsp;
                  </strong>
                  {node.fieldSize!.entity!.name}
                </p>
              )}
              {node.fieldRole!.length > 0 && (
                <p>
                  <strong>
                    {i18n('mktg-ct-cs-category-3')}
                    :&nbsp;
                  </strong>
                  {(node.fieldRole as Drupal_FieldNodeCaseStudyFieldRole[]).map(
                    (role, i) => {
                      const roleEntity = role.entity as Drupal_TaxonomyTermRole;
                      return `${roleEntity.name}${
                        i < node.fieldRole!.length - 1 ? ', ' : ''
                        }`;
                    }
                  )}
                </p>
              )}
            </Box>
            {node.fieldSidebarSummary && (
              <Box
                css={summaryCss}
                dangerouslySetInnerHTML={{
                  __html: getProcessedValue(node.fieldSidebarSummary)
                }}
              />
            )}
          </Box>
        </Box>
        <Box
          css={(theme: LucidTheme) => ({
            flex: '1 1 810px',
            zIndex: theme.zIndices[2],
            [mqMax.tabletSmall]: {
              flex: '0 1 100%',
              maxWidth: '100%'
            }
          })}
        >
          <img
            alt="Banner"
            css={{
              [mqMax.tabletSmall]: {
                display: 'none'
              }
            }}
            loading="lazy"
            src={node.fieldHeroImage!}
          />
          <Box
            css={(theme: LucidTheme) => ({
              padding: `${theme.space[5]}px ${theme.space[10]}px`,
              maxWidth: 890,
              position: 'relative',
              [mqMax.maxWidth]: {
                padding: `${theme.space[5]}px 12.5%`
              },
              [mqMax.tabletSmall]: {
                maxWidth: '100%',
                padding: `${theme.space[5]}px 8.5%`
              },

              h2: {
                fontSize: theme.fontSizes[6],
                lineHeight: theme.lineHeights.body,
                fontWeight: theme.fontWeights.subheading,
                margin: theme.space[0],
                flex: '0 1 100%',
                color: theme.colors.coolGrey[100],
                [mqMax.tabletSmall]: {
                  fontSize: theme.fontSizes[5]
                }
              },
              h3: {
                fontSize: theme.fontSizes[4],
                lineHeight: theme.lineHeights.body,
                fontWeight: theme.fontWeights.subheading,
                margin: theme.space[0],
                color: theme.colors.coolGrey[100],

                [mqMax.tabletSmall]: {
                  fontSize: theme.fontSizes[3]
                }
              },
              img: {
                maxWidth: '100%',
                height: 'auto',
                margin: `${theme.space[7]}px ${theme.space[0]}`
              },
              '.extend': {
                position: 'relative',
                width: 1200,
                right: '74%',
                margin: `${theme.space[7]}px ${theme.space[0]}`,
                display: 'block',
                [mqMax.maxWidth]: {
                  position: 'relative',
                  height: 'auto !important',
                  width: '100%',
                  left: 'auto',
                  right: 'auto',
                  margin: `${theme.space[7]}px ${theme.space[0]}`
                },
                img: {
                  width: 'auto',
                  margin: theme.space[0],
                  padding: theme.space[0],

                  [mqMax.maxWidth]: {
                    maxWidth: '100%',
                    height: 'auto !important'
                  }
                }
              },
              blockquote: {
                fontSize: theme.fontSizes[4],
                lineHeight: theme.lineHeights.body,
                color: theme.colors.teal[50],
                fontWeight: theme.fontWeights.bold,
                marginLeft: theme.space[0],
                marginRight: theme.space[0],
                cite: {
                  fontSize: theme.fontSizes[2],
                  fontWeight: theme.fontWeights.subheading,
                  marginTop: -theme.space[3],
                  display: 'block'
                }
              }
            })}
          >
            {node.body &&
              ReactHtmlParser(getProcessedValue(node.body), {
                transform
              })}
          </Box>
        </Box>
      </Box>
      <AdditionalResources
        blurb={node.fieldFooterBlurb}
        ctas={(node.fieldAdditionalResourcesCtas as Drupal_FieldNodeCaseStudyFieldAdditionalResourcesCtas[]).map(
          ({ entity }) => entity as Drupal_ParagraphCtaButton
        )}
        resources={(node.fieldAdditionalResources as Drupal_FieldNodeCaseStudyFieldAdditionalResources[]).map(
          ({ entity }) => entity as Drupal_ParagraphAdditionalResources
        )}
      />
    </Layout>
  );
};

export default CaseStudy;

export const query = graphql`
  query($nid: String!, $LanguageId: Drupal_LanguageId!, $i18nKey: String!) {
    drupal {
      nodeReference: nodeById(id: "996", language: $LanguageId) {
        entityUrl {
          path
        }
      }
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeCaseStudy {
          ...entityFields
          title
          body {
            processed
          }
          fieldTitle
          fieldLogo
          fieldCompanyName
          fieldHeroText
          fieldHeroImage
          fieldKeyBenefitsTitle
          fieldHeaderCta {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      entityBundle
                      ... on Drupal_ParagraphMarketoForm {
                        fieldMarketoId
                      }
                    }
                  }
                }
              }
            }
          }
          fieldIndustry {
            entity {
              name
            }
          }
          fieldRole {
            entity {
              name
            }
          }
          fieldSize {
            entity {
              name
            }
          }
          fieldPersona {
            entity {
              entityLabel
            }
          }
          fieldSidebarSummary {
            processed
          }
          fieldKeyBenefits {
            entity {
              ... on Drupal_ParagraphKeyBenefits {
                fieldBenefitName
                fieldBenefitDescription
              }
            }
          }
          fieldAdditionalResources {
            entity {
              ... on Drupal_ParagraphAdditionalResources {
                fieldResourceUrl
                fieldResourceText
              }
            }
          }
          fieldAdditionalResourcesCtas {
            entity {
              ... on Drupal_ParagraphCtaButton {
                fieldButtonText
                fieldButtonLink
                fieldIsSecondary
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldFooterBlurb
        }
      }
    }
    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_ct_cs_back_to_hub
            mktg_ct_cs_key_benefits
            mtkg_submit_policy
            mktg_ct_all_get_a_quote
            mktg_ct_all_request_a_demo
            mktg_ct_cs_category_1
            mktg_ct_cs_category_2
            mktg_ct_cs_category_3
            mktg_ct_cs_additional_resources
            mktg_ct_cs_prefooter_title
            mktg_ct_cs_studies_header
          }
        }
      }
    }
  }
`;
